import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { InviteMemberButton } from "@components/common/invite-member-button";
import {
  createMemberOption,
  createTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { EnableGroupCreationCheckbox } from "@components/groups/enable-group-creation-checkbox";
import { SelectGroupRole } from "@components/role/select-group-role";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseGroupDetailsProps } from "@custom-types/group-types";
import { RoleDisplayNames } from "@custom-types/member-types";
import { type AutoCompleteOption, Dialog } from "@flat-ui";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { Stack } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { selectedGroupMembersSelector } from "@store/groups/groups-selector";
import {
  addProcessingGroups,
  removeProcessingGroups,
  setOne,
} from "@store/groups/groups-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { delay } from "@utils/time-utils";
import { GroupMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { createGroupsPermission } from "@utils/user-utils";
import { useCallback, useMemo, useState } from "react";
import { TeamId, UserId } from "@custom-types/base-types";
import { useTeamUtils } from "@hooks/use-team-utils";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { sortTeams } from "@utils/team-utils";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";

interface Props extends BaseGroupDetailsProps {
  /**
   * Determines the variant of the invite member button
   *  - "outlined" (default): Contained button with white background and blue border
   *  - "filled": Normal button with blue background
   */
  buttonVariant?: "outlined" | "filled";
}

/** Renders the invite member button and the functionality in member page */
export function InviteMemberToGroup({
  group,
  buttonVariant,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { groupId } = useAppParams();
  const companyId = useCompanyId();
  const { respondToUser } = useRespondToInvite();
  const { isTeamName } = useTeamUtils();
  const { trackEvent } = useTrackEvent();

  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(teamsSelector);
  const groupMembers = useAppSelector(selectedGroupMembersSelector);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shouldCreateGroups, setShouldCreateGroups] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] =
    useState<FaroCoreApiTypes.GroupRole>("PROJECT_MANAGER");

  /** Checks if a user or team is already in the group */
  const isAlreadyInGroup = useCallback(
    (id: UserId | TeamId) =>
      groupMembers.find((groupMember) => id === groupMember.identity),
    [groupMembers]
  );

  /** Reformation company members data for faro autocomplete component */
  const memberOptions: AutoCompleteOption[] = useMemo(
    () =>
      sortMembersByNameOrEmail(companyMembers).map((member) => {
        const memberInGroup = isAlreadyInGroup(member.identity);
        const isMemberInGroup = !!memberInGroup;

        return createMemberOption({
          member,
          isDisabled: isMemberInGroup,
          disabledMessage: isMemberInGroup
            ? `Already in group ${
                memberInGroup?.role
                  ? `as ${RoleDisplayNames[memberInGroup?.role]}`
                  : ""
              }`
            : "",
        });
      }),
    [companyMembers, isAlreadyInGroup]
  );

  /** Reformation teams data for faro autocomplete component */
  const teamOptions: AutoCompleteOption[] = useMemo(
    () =>
      sortTeams(teams, "name_ascending").map((team) => {
        const teamInGroup = isAlreadyInGroup(team.id);
        const isTeamInGroup = !!teamInGroup;

        return createTeamOption({
          team,
          isDisabled: isTeamInGroup,
          disabledMessage: isTeamInGroup
            ? `Already in group ${
                teamInGroup?.role
                  ? `as ${RoleDisplayNames[teamInGroup?.role]}`
                  : ""
              }`
            : "",
        });
      }),
    [isAlreadyInGroup, teams]
  );

  const autocompleteOptions: AutoCompleteOption[] = useMemo(
    () => [...teamOptions, ...memberOptions],
    [teamOptions, memberOptions]
  );

  const {
    selectedMembers,
    selectedValues,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset,
  } = useMembersAutocomplete({
    entityName: "group",
    existingMembers: group.members,
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map((member) => member.identity),
  });

  // Disable the `Send Invite` button if no user has been selected and the input is not a valid email.
  const shouldSendInviteDisable = shouldDisableSendInvite(
    uniqueSelectedValues,
    isMemberInputValid
  );

  /** Close the dialog and reset loading state */
  async function closeDialog(): Promise<void> {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm(): Promise<void> {
    setIsLoading(true);

    trackEvent({
      name: GroupMemberEvents.inviteMembers,
      props: {
        numberOfMembers: uniqueSelectedValues.length,
        role: selectedRole,
      },
    });

    try {
      await createPayloadAndSendInvite();

      void closeDialog();
    } catch (error) {
      setIsLoading(false);

      handleErrorWithToast({
        id: `inviteMember-${Date.now().toString()}`,
        title: "Cannot invite member(s)",
        error,
      });
    }
  }

  /** This function creates payload and call the appropriate route */
  async function createPayloadAndSendInvite(): Promise<void> {
    // Additional permission to be added if user should be able to create groups
    const permission: FaroCoreApiTypes.UserPermission | undefined =
      shouldCreateGroups && selectedRole === "COMPANY_MANAGER"
        ? createGroupsPermission
        : undefined;

    const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"group"> = {
      assignments: [
        {
          identities: uniqueSelectedValues,
          permission,
          role: selectedRole,
        },
      ],
    };

    if (groupId && uniqueSelectedValues.length) {
      // Add the groupId to processing groups to start the loading spinner in the group
      dispatch(addProcessingGroups([groupId]));

      const response: FaroCoreApiTypes.AddMembersToGroupResponse =
        await faroCoreApiClient.groups.addMembersToGroup({
          companyId,
          groupId,
          payload,
        });

      // Add invited member to the store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        const groupWithUpdatedMembers = {
          ...group,
          members: response.data.members,
        };

        dispatch(setOne(groupWithUpdatedMembers));
      }

      // Remove groupId from processing group to stop the loading spinner in the group
      dispatch(removeProcessingGroups([groupId]));

      respondToUser(response, uniqueSelectedValues.length);
    }
  }

  /** Default setup when invite dialog open */
  function onOpenInviteDialog(): void {
    reset();
    setIsDialogOpen(true);
  }

  return (
    <>
      <InviteMemberButton
        buttonVariant={buttonVariant}
        onClick={onOpenInviteDialog}
      />

      <Dialog
        title="Invite members to group"
        confirmText="Send Invite"
        open={isDialogOpen}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        isConfirmDisabled={shouldSendInviteDisable}
        onCancel={() => setIsDialogOpen(false)}
      >
        <Stack marginBottom={SPACE_ELEMENTS_OF_MODAL}>
          <SelectGroupRole
            selectedRole={selectedRole}
            onChange={setSelectedRole}
            variant="outlined"
          />
        </Stack>
        <Stack>
          <MembersAutocomplete
            isRequired
            hasAutoFocus
            label="Members"
            helperText={helperText}
            options={autocompleteOptions}
            onInputChange={onInputChange}
            selectedOptions={selectedMembers}
            handleMemberSelect={handleMemberSelect}
            isValid={(value) => isNewEmailValid(value) || isTeamName(value)}
          />
        </Stack>

        <EnableGroupCreationCheckbox
          selectedRole={selectedRole}
          shouldCreateGroups={shouldCreateGroups}
          membersCount={selectedValues.length}
          setShouldCreateGroups={setShouldCreateGroups}
        />
      </Dialog>
    </>
  );
}
